import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatTarih } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import Ipucu from '../../ui/Ipucu';

/**
 * AYARLAR › WHATSAPP — kurulum durum paneli + şablon yönetimi + deneme iletisi.
 *
 * Kullanıcının derdi prosedür: ekranın tepesi "şu an hangi adımdasınız, ne eksik" der.
 * Panelin KENDİ ölçebildiği adımlar kendiliğinden işaretlenir; ölçemediklerinde "tamamladım"
 * kutusu vardır. Anahtar / jeton / sır DEĞERLERİ bu ekranda hiç görünmez — yalnız satır adları.
 * Sağlayıcıya göre adım listesi değişir (aracı firma: 5 adım · Meta doğrudan: 7 adım); ekran
 * sağlayıcıya özgü hiçbir kural bilmez, listeyi sunucudan alır (`/api/whatsapp/durum`).
 */
type Adim = { id: string; baslik: string; durum: 'tamam' | 'eksik'; otomatik: boolean; ayrinti: string | null };
type Esleme = { ad: string; dil: string; govde: string; parametreler: string[] };
type Ayar = { sablonlar: { geciken: Esleme; yaklasan: Esleme }; tamam: Record<string, boolean> };
type SablonOzeti = { tur: 'geciken' | 'yaklasan'; ad: string; dil: string; durum: string; hamDurum: string | null; neden: string | null; kullanilabilir: boolean; engel: string | null; olusturulabilir: boolean };
type Durum = {
  saglayici: { ad: string; etiket: string; araci: boolean } | null;
  hazir: boolean; secili: 'araci' | 'meta' | null;
  env: { ad: string; var: boolean; zorunlu: boolean; ne: string }[];
  webhook: { adres: string; hazir: boolean; sonOlay: string | null; kendisiAyarlar: boolean; elSikismali: boolean };
  baglanti: { at: string; ok: boolean; numara: string | null; ad: string | null; kalite: string | null; hata: string | null } | null;
  sablonDurumu: { at: string | null; hata: string | null };
  sablonlar: SablonOzeti[];
  ayar: Ayar; varsayilan: Ayar;
  yerTutucular: { ad: string; aciklama: string; ornek: string }[];
  yollar: { araci: Adim[]; meta: Adim[] };
};

const hataMetni = (e: unknown, yedek: string) => (e instanceof Error ? e.message : yedek);
const TUR_AD: Record<string, string> = { geciken: 'Vadesi gelen / geçen', yaklasan: 'Vadesi yaklaşan' };
const ROZET: Record<string, { ad: string; sinif: string }> = {
  APPROVED: { ad: 'Onaylandı', sinif: 'ccd-badge-success' }, PENDING: { ad: 'İnceleniyor', sinif: 'ccd-badge-info' }, REJECTED: { ad: 'Reddedildi', sinif: 'ccd-badge-danger' },
  DIGER: { ad: 'Kullanılamıyor', sinif: 'ccd-badge-danger' }, YOK: { ad: 'Henüz gönderilmedi', sinif: 'ccd-badge-warning' }, BILINMIYOR: { ad: 'Bilinmiyor (kanal bağlı değil)', sinif: '' },
};

/**
 * "Nasıl yapılır" metinleri. Menü adları sağlayıcıların ekranlarından HATIRLANARAK yazıldı;
 * "(yaklaşık)" yazanlar birebir tutmayabilir — ekranlar sık değişir. Ayrıntı: docs/WHATSAPP.md.
 */
const NASIL: Record<string, string[]> = {
  'araci-hesap': [
    'Aracı firmanın sitesinden işletme hesabı açın (360dialog için: hub.360dialog.com — yaklaşık).',
    'Kayıt sırasında firma sizi Meta\'nın "WhatsApp Business hesabı oluştur" penceresine götürür; Facebook hesabınızla girip işletme bilgilerini doldurun. İşletme doğrulamasını (vergi levhası vb.) aracı firmanın ekranındaki adımlar yürütür.',
    'WhatsApp\'a bağlanacak telefon numarasını girin ve SMS ya da sesli aramayla gelen kodu yazın.',
    'DİKKAT: bu numara telefondaki WhatsApp ya da WhatsApp Business uygulamasında KAYITLI OLMAMALI. Kayıtlıysa önce uygulamadan hesabı silin (Ayarlar › Hesap › Hesabımı sil) ya da yeni bir numara kullanın. Numara API\'ye bağlandıktan sonra telefondaki uygulamada kullanılamaz.',
    'Hesap açılınca aşağıdaki kutuyu işaretleyin.',
  ],
  'meta-isletme': [
    'business.facebook.com adresinde Meta Business hesabı (işletme portföyü) açın ya da var olana girin.',
    'Ayarlar › İşletme bilgileri (Business info — yaklaşık) › "Doğrulamayı başlat": işletmenin resmî adı, adresi, vergi levhası / ticaret sicil belgesi istenir. İnceleme birkaç iş günü sürebilir.',
    'developers.facebook.com › "Uygulama oluştur" › tür: İşletme (Business) › uygulamaya "WhatsApp" ürününü ekleyin. Uygulamayı işletme hesabınıza bağlayın.',
    'Doğrulama tamamlanmadan da deneme yapılabilir, ancak günlük gönderim sınırı çok düşüktür. Doğrulama bitince kutuyu işaretleyin.',
  ],
  'meta-numara': [
    'Uygulama panosu › WhatsApp › "API Kurulumu" (API Setup — yaklaşık) › "Telefon numarası ekle".',
    'Görünen ad (müşterinin göreceği firma adı), kategori ve numarayı girin; SMS ya da sesli aramayla gelen kodu yazın.',
    'DİKKAT: bu numara telefondaki WhatsApp ya da WhatsApp Business uygulamasında KAYITLI OLMAMALI. Kayıtlıysa önce uygulamadan hesabı silin ya da yeni bir numara kullanın.',
    'Aynı sayfada iki kimlik görünür: "Telefon numarası kimliği" (Phone number ID) ve "WhatsApp Business hesabı kimliği" (WABA ID). İkisini de not edin; 4. adımda sunucuya girilecek.',
    'Bilgiler sunucuya girilip "Bağlantıyı sına" çalışınca bu adım kendiliğinden işaretlenir.',
  ],
  'meta-jeton': [
    'business.facebook.com › Ayarlar › Kullanıcılar › "Sistem kullanıcıları" › Ekle (rol: Yönetici).',
    'Sistem kullanıcısına iki varlık atayın: WhatsApp uygulamanız (tam denetim) ve WhatsApp Business hesabınız (tam denetim).',
    '"Jeton oluştur" (Generate token): uygulamanızı seçin, süre: "Hiçbir zaman" (kalıcı), izinler: whatsapp_business_messaging ve whatsapp_business_management.',
    'Çıkan jeton YALNIZ BİR KEZ gösterilir; kopyalayıp doğrudan sunucuya girin. Sohbete, e-postaya ya da bu ekrana yazmayın.',
    'Uygulama sırrı: developers.facebook.com › uygulamanız › Ayarlar › Temel › "Uygulama sırrı" (App secret) › Göster.',
  ],
  env: [
    'Aşağıdaki satırlar sunucudaki .env dosyasına yazılır. Bu işi sunucu yöneticisi yapar; değerleri ona güvenli bir yoldan (yüz yüze, parola yöneticisi) iletin — sohbete ya da e-postaya yazmayın.',
    'Bu ekranda değerler HİÇ görünmez; yalnız "tanımlı / eksik" bilgisi görünür.',
    'Satırlar yazıldıktan sonra panel servisleri yeniden başlatılır; bu sayfayı yenileyince eksik satır kalmadığını görürsünüz. Sonra "Bağlantıyı sına"ya basın.',
  ],
  'webhook-araci': [
    'Webhook, müşterinin yazdığı yanıtın ve "teslim edildi / okundu" bilgisinin panele düştüğü adrestir.',
    'En kolayı: aşağıdaki "Webhook\'u ayarla" düğmesi. Panel, aracı firmaya kendi adresini ve gizli başlığı yazar.',
    'Elle yapacaksanız: aracı firmanın ekranında "Webhook adresi" alanına aşağıdaki adresi, özel başlık alanına x-webhook-sir başlığını ve sunucudaki WHATSAPP_WEBHOOK_SIR değerini girin. Başlık girilemiyorsa sır adresin sonuna eklenir: …/api/whatsapp/webhook/<sır>.',
    'Deneme iletisi gönderdiğinizde ilk olay gelir ve bu adım kendiliğinden işaretlenir.',
  ],
  'webhook-meta': [
    'developers.facebook.com › uygulamanız › WhatsApp › "Yapılandırma" (Configuration) › Webhook › Düzenle.',
    '"Geri çağırma adresi" (Callback URL): aşağıdaki panel adresi. "Doğrulama belirteci" (Verify token): sunucudaki WHATSAPP_WEBHOOK_VERIFY değeri.',
    '"Doğrula ve kaydet"e basın; panel doğru belirteci görünce onaylar.',
    'Aynı sayfada "Webhook alanları"ndan messages alanına ABONE OLUN (Subscribe). Başka alana gerek yok.',
    'Deneme iletisi gönderdiğinizde ilk olay gelir ve bu adım kendiliğinden işaretlenir.',
  ],
  sablon: [
    'WhatsApp\'ta işletme, müşteriye ilk iletiyi ancak önceden ONAYLANMIŞ bir şablonla gönderebilir. Aşağıda iki hazır şablon var: vadesi yaklaşan ve vadesi gelen / geçen.',
    'Metni okuyun; isterseniz düzeltin (tanıtım / kampanya cümlesi eklemeyin, reddedilir). "Onaya gönder"e basın.',
    'Onay genellikle birkaç dakika ile bir gün arasında sürer. "Durumu yenile" ile bakın: Onaylandı / İnceleniyor / Reddedildi.',
    'Reddedilirse nedeni burada yazar; metni düzeltip şablon ADINI değiştirerek (ör. sonuna _2) yeniden gönderin — aynı adla hemen yeniden gönderilemez.',
  ],
  deneme: [
    'Aşağıdaki "Deneme iletisi" kutusuna KENDİ cep numaranızı yazın ve gönderin.',
    '"hello_world" Meta\'nın her hesapta hazır gelen İngilizce örnek şablonudur; kendi şablonlarınız onaylanmadan da denenebilir (aracı firmalarda bulunmayabilir).',
    'Kendi şablonunuzla denerseniz ileti ÖRNEK değerlerle gider ("Örnek Yapı Ltd. Şti.", "32.400,00 TL").',
    'İleti telefonunuza ulaştıysa kurulum tamamdır. Borç Hatırlatma › Ayarlar\'dan WhatsApp kanalını açabilirsiniz.',
  ],
};
const nasilAnahtari = (id: string, yol: 'araci' | 'meta') => (id === 'webhook' ? `webhook-${yol}` : id);

function Bilgi({ metin, tehlike, kapat }: { metin: string; tehlike?: boolean; kapat?: () => void }) {
  return (
    <div className={`ccd-banner${tehlike ? ' ccd-banner-danger' : ''}`} role="status">
      <span className="ccd-banner-icon" aria-hidden="true">{tehlike ? '!' : '✓'}</span>
      <span style={{ flex: 1 }}>{metin}</span>
      {kapat && <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat} aria-label="Kapat">✕</button>}
    </div>
  );
}

const doldur = (govde: string, p: string[]) => govde.replace(/\{\{\s*(\d+)\s*\}\}/g, (hepsi, n: string) => p[Number(n) - 1] ?? hepsi);

export default function WhatsappAyar() {
  const { can } = useAuth();
  const yazar = can('reminder.settings');
  const [d, setD] = useState<Durum | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<{ metin: string; tehlike: boolean } | null>(null);
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [yol, setYol] = useState<'araci' | 'meta'>('araci');
  const [acik, setAcik] = useState<string | null>(null);
  const [taslak, setTaslak] = useState<Ayar | null>(null);
  const [deneme, setDeneme] = useState<{ hedef: string; sablon: 'hello_world' | 'geciken' | 'yaklasan' }>({ hedef: '', sablon: 'hello_world' });

  const yukle = useCallback(() => {
    api<Durum>('/api/whatsapp/durum').then((v) => {
      setD(v); setHata(null);
      setTaslak((eski) => eski ?? v.ayar);
      if (v.secili) setYol(v.secili);
    }).catch((e) => setHata(hataMetni(e, 'WhatsApp durumu okunamadı')));
  }, []);
  useEffect(() => { yukle(); }, [yukle]);

  const adimlar = d ? d.yollar[yol] : [];
  const siradaki = adimlar.find((a) => a.durum === 'eksik') ?? null;
  const tamamAdet = adimlar.filter((a) => a.durum === 'tamam').length;
  const eslemeDegisti = useMemo(() => Boolean(d && taslak && JSON.stringify(taslak.sablonlar) !== JSON.stringify(d.ayar.sablonlar)), [d, taslak]);

  async function calistir(ad: string, is: () => Promise<string | void>) {
    setMesgul(ad); setBilgi(null);
    try { const m = await is(); if (m) setBilgi({ metin: m, tehlike: false }); yukle(); }
    catch (e) { setBilgi({ metin: hataMetni(e, 'İşlem yapılamadı'), tehlike: true }); yukle(); }
    finally { setMesgul(null); }
  }

  const isaretle = (id: string, tamam: boolean) => calistir('adim', async () => { await api('/api/whatsapp/adim', { method: 'PUT', body: JSON.stringify({ id, tamam }) }); });
  const baglantiSina = () => calistir('baglanti', async () => {
    const r = await api<{ ad: string | null; numara: string | null; kalite: string | null }>('/api/whatsapp/baglanti-sina', { method: 'POST', body: '{}' });
    return `Bağlantı çalışıyor${r.ad || r.numara ? `: ${[r.ad, r.numara, r.kalite && `kalite ${r.kalite}`].filter(Boolean).join(' · ')}` : '.'}`;
  });
  const webhookAyarla = () => {
    if (!d || !window.confirm(`Panel, ${d.saglayici?.etiket ?? 'sağlayıcı'} hesabınızdaki webhook adresini şu adresle DEĞİŞTİRECEK:\n\n${d.webhook.adres}\n\nHesapta başka bir sisteme giden webhook varsa o artık olay almaz. Devam edilsin mi?`)) return;
    void calistir('webhook', async () => (await api<{ not: string }>('/api/whatsapp/webhook-ayarla', { method: 'POST', body: JSON.stringify({ onay: true }) })).not);
  };
  const eslemeKaydet = () => calistir('esleme', async () => {
    if (!taslak || !d) return;
    const r = await api<{ ayar: Ayar }>('/api/whatsapp/ayar', { method: 'PUT', body: JSON.stringify({ ...taslak, tamam: d.ayar.tamam }) });
    setTaslak(r.ayar);
    return 'Şablon eşlemesi kaydedildi.';
  });
  const sablonGonder = (turler: ('geciken' | 'yaklasan')[]) => {
    if (!d || !taslak) return;
    if (eslemeDegisti) { setBilgi({ metin: 'Önce eşlemedeki değişikliği kaydedin; onaya kaydedilmiş metin gider.', tehlike: true }); return; }
    const adlar = turler.map((t) => `• ${d.ayar.sablonlar[t].ad}`).join('\n');
    if (!window.confirm(`Şu şablon(lar) ${d.saglayici?.etiket ?? 'sağlayıcı'} üzerinden Meta'nın ONAYINA gönderilecek:\n\n${adlar}\n\nGönderilen şablonun metni sonradan değiştirilemez (yeni adla yeniden gönderilir). Devam edilsin mi?`)) return;
    void calistir('sablon', async () => {
      const r = await api<{ ok: boolean; sonuclar: { ad: string; ok: boolean; hata: string | null }[] }>('/api/whatsapp/sablon/gonder', { method: 'POST', body: JSON.stringify({ turler, onay: true }) });
      const kotu = r.sonuclar.filter((x) => !x.ok);
      if (kotu.length) throw new Error(kotu.map((x) => `${x.ad}: ${x.hata}`).join(' · '));
      return 'Şablonlar onaya gönderildi. Onay birkaç dakika ile bir gün arasında sürebilir; "Durumu yenile" ile izleyin.';
    });
  };
  const sablonYenile = () => calistir('yenile', async () => { await api('/api/whatsapp/sablon/yenile', { method: 'POST', body: '{}' }); return 'Şablon durumları yenilendi.'; });
  const denemeGonder = () => calistir('deneme', async () => {
    const r = await api<{ durum: string }>('/api/whatsapp/deneme', { method: 'POST', body: JSON.stringify(deneme) });
    if (r.durum !== 'GONDERILDI') throw new Error('WhatsApp bağlı değil (deneme kipi): hiçbir şey gönderilmedi. Önce sunucuya sağlayıcı bilgileri girilmeli.');
    return `Deneme iletisi ${deneme.hedef} numarasına gönderildi. Telefonunuza ulaştığını doğrulayın; birkaç saniye içinde aşağıda "son olay" zamanı da değişmeli.`;
  });
  const kopyala = (metin: string, ne: string) => {
    void navigator.clipboard?.writeText(metin).then(() => setBilgi({ metin: `${ne} panoya kopyalandı.`, tehlike: false }), () => setBilgi({ metin: 'Kopyalanamadı; metni elle seçip kopyalayın.', tehlike: true }));
  };

  if (hata && !d) return <Bilgi metin={hata} tehlike />;
  if (!d || !taslak) return <div className="ccd-inline-note">Yükleniyor…</div>;

  const eslemeYaz = (tur: 'geciken' | 'yaklasan', alan: keyof Esleme, deger: string | string[]) =>
    setTaslak({ ...taslak, sablonlar: { ...taslak.sablonlar, [tur]: { ...taslak.sablonlar[tur], [alan]: deger } } });
  const ornekDeger = (ad: string) => d.yerTutucular.find((y) => y.ad === ad)?.ornek ?? ad;

  return (
    <div className="ccd-stack" data-tur="whatsapp-ayar">
      {bilgi && <Bilgi metin={bilgi.metin} tehlike={bilgi.tehlike} kapat={() => setBilgi(null)} />}
      {!yazar && <div className="ccd-meta">Kurulumu yalnız yönetici yapabilir; burada salt okunur görüyorsunuz.</div>}

      {/* ─── Özet ─────────────────────────────────────────────────────── */}
      <div className="ccd-card ccd-stack-sm" id="whatsapp-durum">
        <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div className="ccd-fieldset-title">Kurulum durumu <Ipucu id="whatsapp-kurulum" /></div>
            <div className="ccd-wa-ozet">
              {d.hazir
                ? siradaki ? <>Kanal bağlı. Sıradaki adım: <b>{siradaki.baslik}</b></> : <>Kurulum tamam: WhatsApp ile hatırlatma gönderilebilir.</>
                : d.saglayici ? <>Sağlayıcı seçili ama bilgiler eksik. Sıradaki adım: <b>{siradaki?.baslik}</b></>
                : <>WhatsApp henüz kurulmadı. İletiler <b>deneme kipinde</b>: kayda yazılır, kimseye gitmez.</>}
            </div>
          </div>
          <div className="ccd-row" style={{ gap: 6, flexWrap: 'wrap' }}>
            <span className={`ccd-badge ${d.hazir ? 'ccd-badge-success' : 'ccd-badge-warning'}`}><span className="ccd-badge-dot" />{d.hazir ? 'bağlı — gerçekten gönderir' : 'deneme kipi'}</span>
            <span className="ccd-badge">{d.saglayici ? d.saglayici.etiket : 'sağlayıcı seçilmedi'}</span>
            <span className="ccd-badge">{tamamAdet} / {adimlar.length} adım</span>
          </div>
        </div>
        {!d.saglayici && (
          <>
            <div className="ccd-meta">
              İki yol var. <b>Aracı firma</b> (Meta'nın resmî iş ortağı) ile kurulum daha kısadır: Meta doğrulamasını firma kendi ekranından yürütür.
              <b> Meta doğrudan</b> yolunda işletme doğrulaması, uygulama ve jeton işlerini kendiniz yaparsınız. Hangisini seçeceğiniz sunucudaki
              <span className="ccd-mono"> WHATSAPP_SAGLAYICI</span> satırıyla belirlenir; aşağıda iki yolun adımlarına da bakabilirsiniz.
            </div>
            <div className="ccd-tabs ccd-borc-suzgec" role="tablist" aria-label="Kurulum yolu">
              <button type="button" role="tab" aria-selected={yol === 'araci'} className="ccd-tab" onClick={() => { setYol('araci'); setAcik(null); }}>Aracı firma ile (5 adım)</button>
              <button type="button" role="tab" aria-selected={yol === 'meta'} className="ccd-tab" onClick={() => { setYol('meta'); setAcik(null); }}>Meta doğrudan (7 adım)</button>
            </div>
          </>
        )}

        {/* ─── Adımlar ────────────────────────────────────────────────── */}
        <ol className="ccd-wa-adimlar">
          {adimlar.map((a, i) => {
            const acikMi = acik === a.id;
            const nasil = NASIL[nasilAnahtari(a.id, yol)] ?? [];
            return (
              <li key={a.id} className={`ccd-wa-adim${a.durum === 'tamam' ? ' ccd-wa-tamam' : ''}${siradaki?.id === a.id ? ' ccd-wa-siradaki' : ''}`}>
                <span className="ccd-wa-no" aria-hidden="true">{a.durum === 'tamam' ? '✓' : i + 1}</span>
                <div className="ccd-wa-govde">
                  <div className="ccd-wa-baslik">
                    <b>{a.baslik}</b>
                    <span className={`ccd-badge ${a.durum === 'tamam' ? 'ccd-badge-success' : siradaki?.id === a.id ? 'ccd-badge-warning' : ''}`}>
                      {a.durum === 'tamam' ? 'tamam' : siradaki?.id === a.id ? 'sıradaki adım' : 'bekliyor'}
                    </span>
                    <span className="ccd-meta">{a.otomatik ? 'panel kendisi ölçer' : 'siz işaretlersiniz'}</span>
                  </div>
                  {a.ayrinti && <div className="ccd-meta">{a.ayrinti}</div>}
                  <div className="ccd-row" style={{ gap: 10, flexWrap: 'wrap' }}>
                    <button type="button" className="ccd-borc-bag" aria-expanded={acikMi} onClick={() => setAcik(acikMi ? null : a.id)}>{acikMi ? 'açıklamayı gizle' : 'nasıl yapılır?'}</button>
                    {!a.otomatik && yazar && (
                      <label className="ccd-borc-kanal ccd-meta">
                        <input type="checkbox" checked={a.durum === 'tamam'} disabled={mesgul != null} onChange={(e) => void isaretle(a.id, e.target.checked)} /> tamamladım
                      </label>
                    )}
                  </div>
                  {acikMi && (
                    <div className="ccd-wa-nasil">
                      <ol>{nasil.map((s, n) => <li key={n}>{s}</li>)}</ol>

                      {a.id === 'env' && (
                        <>
                          {d.env.length === 0 ? (
                            <div className="ccd-meta">
                              {yol === 'araci'
                                ? <>Aracı firma (360dialog) için girilecek satırlar: <span className="ccd-mono">WHATSAPP_SAGLAYICI=360dialog</span>, <span className="ccd-mono">WHATSAPP_D360_KEY</span>, <span className="ccd-mono">WHATSAPP_WEBHOOK_SIR</span>.</>
                                : <>Meta doğrudan için girilecek satırlar: <span className="ccd-mono">WHATSAPP_SAGLAYICI=meta</span>, <span className="ccd-mono">WHATSAPP_TOKEN</span>, <span className="ccd-mono">WHATSAPP_PHONE_ID</span>, <span className="ccd-mono">WHATSAPP_WABA_ID</span>, <span className="ccd-mono">WHATSAPP_APP_SECRET</span>, <span className="ccd-mono">WHATSAPP_WEBHOOK_VERIFY</span>.</>}
                            </div>
                          ) : (
                            <div className="ccd-wa-env">
                              {d.env.map((e) => (
                                <div key={e.ad}>
                                  <span className="ccd-mono">{e.ad}</span>
                                  <span className="ccd-meta">{e.ne}{e.zorunlu ? '' : ' (isteğe bağlı)'}</span>
                                  <span className={`ccd-badge ${e.var ? 'ccd-badge-success' : e.zorunlu ? 'ccd-badge-danger' : ''}`}>{e.var ? 'tanımlı' : e.zorunlu ? 'eksik' : 'boş'}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {yazar && <button type="button" className="ccd-btn ccd-btn-sm" disabled={!d.hazir || mesgul != null} title={d.hazir ? undefined : 'Önce satırlar sunucuya girilmeli'}
                                            onClick={() => void baglantiSina()}>{mesgul === 'baglanti' ? 'Sınanıyor…' : 'Bağlantıyı sına'}</button>}
                          {d.baglanti && <div className="ccd-meta">Son sınama: {formatTarih(d.baglanti.at)} — {d.baglanti.ok ? 'çalışıyor' : `hata: ${d.baglanti.hata}`}</div>}
                        </>
                      )}

                      {(a.id === 'meta-numara' || a.id === 'meta-jeton') && yazar && (
                        <button type="button" className="ccd-btn ccd-btn-sm" disabled={!d.hazir || mesgul != null} title={d.hazir ? undefined : 'Önce 4. adımdaki satırlar sunucuya girilmeli'}
                                onClick={() => void baglantiSina()}>{mesgul === 'baglanti' ? 'Sınanıyor…' : 'Bağlantıyı sına'}</button>
                      )}

                      {a.id === 'webhook' && (
                        <>
                          <div className="ccd-wa-kopya">
                            <span className="ccd-field-label">Panelin webhook adresi</span>
                            <code className="ccd-mono">{d.webhook.adres}</code>
                            <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => kopyala(d.webhook.adres, 'Webhook adresi')}>Kopyala</button>
                          </div>
                          <div className="ccd-meta">
                            {yol === 'meta' ? <>Abone olunacak alan: <span className="ccd-mono">messages</span>.</> : <>Sır başlığı: <span className="ccd-mono">x-webhook-sir</span> (değeri sunucudaki <span className="ccd-mono">WHATSAPP_WEBHOOK_SIR</span>).</>}
                            {' '}Son olay: {d.webhook.sonOlay ? formatTarih(d.webhook.sonOlay) : 'henüz gelmedi'}.
                          </div>
                          {yazar && d.webhook.kendisiAyarlar && (
                            <button type="button" className="ccd-btn ccd-btn-sm" disabled={!d.hazir || !d.webhook.hazir || mesgul != null}
                                    title={!d.webhook.hazir ? 'Önce sunucuya WHATSAPP_WEBHOOK_SIR girilmeli' : undefined}
                                    onClick={webhookAyarla}>{mesgul === 'webhook' ? 'Yazılıyor…' : 'Webhook\'u ayarla'}</button>
                          )}
                        </>
                      )}

                      {a.id === 'sablon' && <a className="ccd-borc-bag" href="#whatsapp-sablon">aşağıdaki şablon bölümüne git ↓</a>}
                      {a.id === 'deneme' && <a className="ccd-borc-bag" href="#whatsapp-deneme">aşağıdaki deneme kutusuna git ↓</a>}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
        <div className="ccd-meta">
          Adım adım rehberin tamamı ve yeni bir aracı firma ekleme: depodaki <span className="ccd-mono">docs/WHATSAPP.md</span>.
          Kanal açma / kapama: <Link className="ccd-link" to="/ayarlar/borc-hatirlatma#hatirlatma-kanal">Borç hatırlatma › Kanallar</Link>.
        </div>
      </div>

      {/* ─── Şablonlar ──────────────────────────────────────────────── */}
      <div className="ccd-card ccd-stack-sm" id="whatsapp-sablon">
        <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div className="ccd-fieldset-title">Şablonlar ve eşleme <Ipucu id="whatsapp-sablon" /></div>
          {yazar && (
            <div className="ccd-row" style={{ gap: 6, flexWrap: 'wrap' }}>
              <button type="button" className="ccd-btn ccd-btn-sm" disabled={!d.hazir || mesgul != null} title={d.hazir ? undefined : 'Kanal bağlı değil'} onClick={() => void sablonYenile()}>{mesgul === 'yenile' ? 'Yenileniyor…' : 'Durumu yenile'}</button>
              <button type="button" className="ccd-btn ccd-btn-primary ccd-btn-sm" disabled={!d.hazir || mesgul != null || d.sablonlar.every((s) => s.durum !== 'YOK')}
                      title={!d.hazir ? 'Kanal bağlı değil' : d.sablonlar.every((s) => s.durum !== 'YOK') ? 'Gönderilmemiş şablon yok' : undefined}
                      onClick={() => sablonGonder(d.sablonlar.filter((s) => s.durum === 'YOK').map((s) => s.tur))}>{mesgul === 'sablon' ? 'Gönderiliyor…' : 'Şablonları onaya gönder'}</button>
            </div>
          )}
        </div>
        <div className="ccd-meta">
          WhatsApp iletisi ŞABLONLA gider: metin önceden Meta'ya onaylatılır, panel yalnız değişkenleri doldurur. Şablon onaylı değilken o türde WhatsApp iletisi üretilmez.
          {d.sablonDurumu.at && <> Durum en son {formatTarih(d.sablonDurumu.at)} okundu.</>}
          {d.hazir && d.sablonDurumu.hata && <span className="ccd-field-error"> Durum okunamadı: {d.sablonDurumu.hata}</span>}
        </div>

        {(['geciken', 'yaklasan'] as const).map((tur) => {
          const e = taslak.sablonlar[tur];
          const s = d.sablonlar.find((x) => x.tur === tur)!;
          const rozet = ROZET[s.durum] ?? ROZET.DIGER;
          const degiskenAdet = new Set([...e.govde.matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map((m) => m[1])).size;
          const gonderilmis = s.durum !== 'YOK' && s.durum !== 'BILINMIYOR';
          return (
            <div key={tur} className="ccd-wa-sablon" data-tur={`whatsapp-sablon-${tur}`}>
              <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
                <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <b>{TUR_AD[tur]}</b>
                  <span className={`ccd-badge ${rozet.sinif}`} title={s.hamDurum ?? undefined}>{rozet.ad}</span>
                  {s.neden && <span className="ccd-field-error">Neden: {s.neden}</span>}
                </div>
                <div className="ccd-row" style={{ gap: 6 }}>
                  <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => kopyala(e.govde, 'Şablon metni')}>Metni kopyala</button>
                  {yazar && s.durum === 'YOK' && s.olusturulabilir && <button type="button" className="ccd-btn ccd-btn-sm" disabled={mesgul != null} onClick={() => sablonGonder([tur])}>Onaya gönder</button>}
                </div>
              </div>
              {d.hazir && s.engel && <div className="ccd-meta ccd-wa-engel">{s.engel}</div>}
              <div className="ccd-borc-sablon">
                <div className="ccd-stack-sm">
                  <div className="ccd-borc-iki">
                    <label className="ccd-field">
                      <span className="ccd-field-label">Şablon adı (Meta'daki)</span>
                      <input className="ccd-input ccd-mono" value={e.ad} disabled={!yazar} onChange={(ev) => eslemeYaz(tur, 'ad', ev.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))} />
                    </label>
                    <label className="ccd-field">
                      <span className="ccd-field-label">Dil kodu</span>
                      <input className="ccd-input ccd-mono" value={e.dil} disabled={!yazar} onChange={(ev) => eslemeYaz(tur, 'dil', ev.target.value.trim())} />
                    </label>
                  </div>
                  <label className="ccd-field">
                    <span className="ccd-field-label">Şablon metni <span className="ccd-muted">({'{{1}}'}, {'{{2}}'}… değişkenleriyle)</span></span>
                    <textarea className="ccd-textarea" rows={7} value={e.govde} disabled={!yazar} onChange={(ev) => eslemeYaz(tur, 'govde', ev.target.value)} />
                    <span className={degiskenAdet !== e.parametreler.length ? 'ccd-field-error' : 'ccd-field-hint'}>
                      {gonderilmis ? 'Bu ad Meta\'ya gönderilmiş: metni değiştirirseniz ADI da değiştirin (yeni şablon olarak onaya gider). ' : ''}
                      Metinde {degiskenAdet}, sırada {e.parametreler.length} değişken var{degiskenAdet !== e.parametreler.length ? ' — sayılar eşit olmalı' : ''}. Değişken cümlenin başında ya da sonunda olamaz.
                    </span>
                  </label>
                  <div className="ccd-field">
                    <span className="ccd-field-label">Parametre sırası <span className="ccd-muted">(hangi değişkene hangi bilgi yazılır)</span></span>
                    <div className="ccd-wa-param">
                      {e.parametreler.map((p, n) => (
                        <div key={n}>
                          <span className="ccd-mono">{`{{${n + 1}}}`}</span>
                          <select className="ccd-select" value={p} disabled={!yazar} aria-label={`${n + 1}. değişken`}
                                  onChange={(ev) => eslemeYaz(tur, 'parametreler', e.parametreler.map((x, m) => (m === n ? ev.target.value : x)))}>
                            {d.yerTutucular.map((y) => <option key={y.ad} value={y.ad}>{`{${y.ad}} — ${y.aciklama}`}</option>)}
                          </select>
                          {yazar && e.parametreler.length > 1 && (
                            <button type="button" className="ccd-borc-cikar" aria-label={`${n + 1}. değişkeni kaldır`} onClick={() => eslemeYaz(tur, 'parametreler', e.parametreler.filter((_, m) => m !== n))}>×</button>
                          )}
                        </div>
                      ))}
                    </div>
                    {yazar && e.parametreler.length < 9 && (
                      <button type="button" className="ccd-borc-bag" style={{ justifySelf: 'start' }} onClick={() => eslemeYaz(tur, 'parametreler', [...e.parametreler, 'tutar'])}>değişken ekle</button>
                    )}
                  </div>
                  {yazar && (
                    <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" style={{ alignSelf: 'flex-start' }}
                            onClick={() => { if (window.confirm('Bu şablonun adı, metni ve parametre sırası panelin önerdiği hâle dönsün mü?')) setTaslak({ ...taslak, sablonlar: { ...taslak.sablonlar, [tur]: d.varsayilan.sablonlar[tur] } }); }}>
                      Önerilen metne dön
                    </button>
                  )}
                </div>
                <div className="ccd-stack-sm">
                  <span className="ccd-field-label">Müşterinin göreceği ileti <span className="ccd-muted">(örnek değerlerle)</span></span>
                  <div className="ccd-borc-onizleme ccd-wa-balon">{doldur(e.govde, e.parametreler.map(ornekDeger))}</div>
                </div>
              </div>
            </div>
          );
        })}
        {yazar && (
          <div className="ccd-borc-arac">
            <span className="ccd-meta">{eslemeDegisti ? 'Kaydedilmemiş eşleme değişikliği var.' : 'Eşlemede değişiklik yok.'}</span>
            <span className="ccd-spacer" />
            <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" disabled={!eslemeDegisti || mesgul != null} onClick={() => setTaslak(d.ayar)}>Vazgeç</button>
            <button className="ccd-btn ccd-btn-primary ccd-btn-sm" disabled={!eslemeDegisti || mesgul != null} onClick={() => void eslemeKaydet()}>{mesgul === 'esleme' ? 'Kaydediliyor…' : 'Eşlemeyi kaydet'}</button>
          </div>
        )}
      </div>

      {/* ─── Deneme iletisi ─────────────────────────────────────────── */}
      {yazar && (
        <div className="ccd-card ccd-stack-sm" id="whatsapp-deneme">
          <div className="ccd-fieldset-title">Deneme iletisi</div>
          <div className="ccd-meta">Kendi numaranıza tek ileti gönderir. Kanal bağlı değilse hiçbir şey gitmez.</div>
          <div className="ccd-row" style={{ gap: 6, flexWrap: 'wrap' }}>
            <input className="ccd-input" style={{ flex: '1 1 180px', maxWidth: 240 }} placeholder="Kendi cep numaranız" inputMode="tel" value={deneme.hedef} onChange={(e) => setDeneme({ ...deneme, hedef: e.target.value })} />
            <select className="ccd-select" style={{ flex: '1 1 220px', maxWidth: 340 }} value={deneme.sablon} aria-label="Deneme şablonu" onChange={(e) => setDeneme({ ...deneme, sablon: e.target.value as typeof deneme.sablon })}>
              <option value="hello_world">hello_world — hazır örnek şablon (İngilizce)</option>
              <option value="geciken">{d.ayar.sablonlar.geciken.ad} — örnek değerlerle</option>
              <option value="yaklasan">{d.ayar.sablonlar.yaklasan.ad} — örnek değerlerle</option>
            </select>
            <button type="button" className="ccd-btn ccd-btn-sm" disabled={deneme.hedef.trim().length < 7 || mesgul != null} onClick={() => void denemeGonder()}>{mesgul === 'deneme' ? 'Gönderiliyor…' : 'Deneme iletisi gönder'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
